/**
 * Helper that allow us to join a classroom using a GraphQL mutation.
 *
 * NOTE: This utility will be used by the all the login types in the future (FB,
 * username/password/apple, clever).
 *
 * For historical reasons this function accepts an array of signup codes, but
 * it should only ever be passed a single code.
 *
 * @example
 * joinClassroomsPostlogin(["ABC123"]).then((joinStudent) => {});
 */
import gql from "graphql-tag";

import type {ApolloGqlClient} from "@repo/data-gql-apollo";
import {gqlOp, type GqlOperation} from "@repo/data-gql-core";
import {Errors, KAError} from "@repo/logging";

import type {JoinClassroomsData} from "../shared/types.ts";

import type {postloginJoinStudentMutationType} from "./__graphql-types__/index.ts";
import type {
    postloginJoinStudentMutation_joinStudent as joinStudent,
    postloginJoinStudentMutation_joinStudent_error as mutationError,
} from "./__graphql-types__/postloginJoinStudentMutation.ts";

export type {postloginJoinStudentMutationType};
export type postloginJoinStudentMutationError = mutationError;
export type postloginJoinStudentMutationErrorCode = mutationError["code"];
export type postloginJoinStudentMutationClassroom = joinStudent["classroom"];

export const JOIN_STUDENT_MUTATION: GqlOperation<
    postloginJoinStudentMutationType["response"],
    postloginJoinStudentMutationType["variables"]
> = gqlOp(gql`
    mutation postloginJoinStudentMutation($classCode: String!) {
        joinStudent(classCode: $classCode) {
            error {
                code
                signupCode
                classroomName
            }
            classroom {
                id
                cacheId
                signupCode
                name
            }
        }
    }
`);

/**
 * Joins a classroom after being logged in.
 *
 * TODO(CLASS-6897): For historical reasons this function accepts an array of
 * signupCodes, but in practice this array only ever contains a single item. We
 * should eventually simplify this parameter to a single `string` item, and
 * propagate this change through all the callers that unnecessarily treat
 * signupCodes as n array.
 *
 * @param {Array<string>} signupCodes An optional signupCode passed in the UI.
 * (This is an array for historical reasons, but it is only allowed to contain
 * a single element.)
 *
 * @returns {Promise<JoinClassroomsData>} The mutation result.
 */
export const joinClassroomsPostlogin = async (
    gqlClient: ApolloGqlClient,
    signupCodes: Array<string>,
) => {
    const signupCode = signupCodes[0];
    try {
        const result = await gqlClient.mutate(JOIN_STUDENT_MUTATION, {
            variables: {classCode: signupCode},
        });
        if (!result.data) {
            throw new KAError("Failed to join a classroom", Errors.Internal);
        }
        return {
            errors: result?.data?.joinStudent?.error
                ? [result.data.joinStudent.error]
                : null,
            studentLists: result?.data?.joinStudent?.classroom
                ? [result.data.joinStudent.classroom]
                : [],
        } satisfies JoinClassroomsData;
    } catch (error) {
        // This generic error is used when we get a graphql error
        // (e.g. network failure).
        throw new KAError("Failed to join a classroom", Errors.Internal);
    }
};
